import { useContext, useState } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { SiteCtx, Btn } from "./SiteParts";

const err = (e) => {
  const d = typeof e.response?.data === "object" ? e.response.data.detail : null;
  if (e.response?.status === 422 && /email/i.test(JSON.stringify(d || ""))) return "Please enter a valid email address.";
  if (Array.isArray(d)) return d.some((x) => String(x.loc).includes("email")) ? "Please enter a valid email address." : "Please check the form and try again.";
  return d || "Something went wrong. Please try again in a moment.";
};

export function ContactLine({ x, pal }) {
  const digits = x.replace(/[^\d+]/g, "");
  const phone = /^\+?\d{8,15}$/.test(digits) && !/[a-z]{3}/i.test(x);
  const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x.trim());
  return (
    <p className="flex gap-3 items-start text-sm flex-wrap"><span className="w-2 h-2 mt-1.5 rounded-full shrink-0" style={{ background: pal.accent }} />
      {phone ? <><a href={`tel:${digits}`} className="underline" data-testid="site-phone-link">{x}</a>
        <a href={`https://wa.me/${digits.replace("+", "")}`} target="_blank" rel="noreferrer" className="underline font-semibold" style={{ color: pal.accent }} data-testid="site-whatsapp-link">WhatsApp</a></>
        : email ? <a href={`mailto:${x.trim()}`} className="underline" data-testid="site-email-link">{x}</a> : x}
    </p>);
}

export function SiteForm({ s, t, r, href }) {
  const site = useContext(SiteCtx);
  const [d, setD] = useState({ name: "", email: "", phone: "", message: "", date: "", website: "" });
  const [st, setSt] = useState({ busy: false, ok: "", err: "" });
  const pal = t.palette, booking = s.type === "booking";
  const inp = "w-full h-10 px-3 text-sm border bg-transparent";
  const box = { borderRadius: r, borderColor: `${pal.muted}44` };
  if (!site?.features?.forms) {
    return (
      <div className="p-6 space-y-3 shadow-xl" style={{ background: pal.surface, borderRadius: r }}>
        {["Your name", "Email", booking ? "Preferred date" : "How can we help?"].map((f) => <div key={f} className="h-10 px-3 flex items-center text-xs opacity-60 border" style={box}>{f}</div>)}
        <Btn t={t} href={href}>{booking ? "Request booking" : "Send message"}</Btn>
        <p className="text-[11px] opacity-60" data-testid="site-form-inactive">This form becomes active on your published website. Messages are saved in your Altiora dashboard.</p>
      </div>);
  }
  const send = async (e) => {
    e.preventDefault();
    setSt({ busy: true, ok: "", err: "" });
    try {
      const res = await axios.post(`${API}/sites/${site.slug}/forms`, { section_id: s.id, ...d });
      setSt({ busy: false, ok: res.data.message, err: "" }); setD({ name: "", email: "", phone: "", message: "", date: "", website: "" });
    } catch (x) { setSt({ busy: false, ok: "", err: err(x) }); }
  };
  const f = (k, ph, type = "text", req = false) => <input className={inp} style={box} type={type} required={req} placeholder={ph} value={d[k]} onChange={(e) => setD({ ...d, [k]: e.target.value })} data-testid={`site-form-${k}`} />;
  return (
    <form onSubmit={send} className="p-6 space-y-3 shadow-xl" style={{ background: pal.surface, borderRadius: r }} data-testid={`site-form-${s.id}`}>
      {f("name", "Your name *", "text", true)}{f("email", "Email *", "email", true)}{f("phone", "Phone (optional)", "tel")}
      {booking && f("date", "Preferred date", "date")}
      <textarea className="w-full px-3 py-2 text-sm border bg-transparent" style={box} rows={3} placeholder={booking ? "Anything we should know?" : "How can we help?"} value={d.message} onChange={(e) => setD({ ...d, message: e.target.value })} data-testid="site-form-message" />
      <input tabIndex={-1} autoComplete="off" className="hidden" value={d.website} onChange={(e) => setD({ ...d, website: e.target.value })} aria-hidden />
      <button disabled={st.busy} className="px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60" style={{ background: pal.primary, borderRadius: r }} data-testid="site-form-submit">{st.busy ? "Sending…" : booking ? "Request booking" : "Send message"}</button>
      {st.ok && <p className="text-sm text-emerald-700" role="status" data-testid="site-form-success">{st.ok}</p>}
      {st.err && <p className="text-sm text-red-700" role="alert" data-testid="site-form-error">{st.err}</p>}
    </form>);
}

const loadRzp = () => new Promise((res, rej) => {
  if (window.Razorpay) return res();
  const sc = document.createElement("script");
  sc.src = "https://checkout.razorpay.com/v1/checkout.js"; sc.onload = res; sc.onerror = () => rej(new Error("Payment window couldn't load."));
  document.body.appendChild(sc);
});

export function BuyButtons({ s, t, r }) {
  const site = useContext(SiteCtx);
  const [msg, setMsg] = useState("");
  const [buyer, setBuyer] = useState({ name: "", email: "", phone: "" });
  const [open, setOpen] = useState(null);
  if (!site?.features?.payments) return null;
  const items = (s.items || []).map((it, i) => ({ ...it, i })).filter((it) => /\d/.test(String(it.meta || it.price || "")) && !String(it.meta || it.price).includes("["));
  if (!items.length) return null;
  const pay = async (e) => {
    e.preventDefault(); setMsg("");
    try {
      await loadRzp();
      const o = (await axios.post(`${API}/sites/${site.slug}/checkout`, { section_id: s.id, index: open, ...buyer })).data;
      const rz = new window.Razorpay({ key: o.key_id, order_id: o.order_id, amount: o.amount, currency: o.currency, description: o.item, prefill: { name: buyer.name, email: buyer.email, contact: buyer.phone },
        handler: async (resp) => { try { setMsg((await axios.post(`${API}/sites/${site.slug}/checkout/verify`, resp)).data.message); setOpen(null); } catch (x) { setMsg(err(x)); } } });
      rz.on("payment.failed", async (f) => { setMsg((await axios.post(`${API}/sites/${site.slug}/checkout/failed`, { razorpay_order_id: o.order_id, client_token: o.client_token, reason: f.error?.description || "" })).data.message); });
      rz.open();
    } catch (x) { setMsg(x.response ? err(x) : x.message); }
  };
  return (
    <div className="mt-6 space-y-3" data-testid={`buy-${s.id}`}>
      {site.features.pay_mode === "test" && <p className="text-xs font-semibold text-amber-700" data-testid="site-test-mode">Test mode — no real money is taken.</p>}
      <div className="flex flex-wrap gap-2">{items.map((it) => <button key={it.i} onClick={() => setOpen(it.i)} className="px-4 py-2 text-sm font-semibold text-white" style={{ background: t.palette.accent, borderRadius: r }} data-testid={`buy-btn-${it.i}`}>Buy {it.title} · {it.meta || it.price}</button>)}</div>
      {open !== null && <form onSubmit={pay} className="grid sm:grid-cols-4 gap-2 max-w-3xl" data-testid="buy-form">
        {["name", "email", "phone"].map((k) => <input key={k} required={k !== "phone"} type={k === "email" ? "email" : "text"} placeholder={k === "phone" ? "Phone" : k === "email" ? "Email *" : "Name *"} value={buyer[k]} onChange={(e) => setBuyer({ ...buyer, [k]: e.target.value })} className="h-10 px-3 text-sm border" style={{ borderRadius: r }} data-testid={`buy-${k}`} />)}
        <button className="h-10 px-4 text-sm font-semibold text-white" style={{ background: t.palette.primary, borderRadius: r }} data-testid="buy-pay">Pay securely</button></form>}
      {msg && <p className="text-sm" role="status" data-testid="buy-message">{msg}</p>}
    </div>);
}
